import { useState, useEffect } from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { defaultFAQs } from '@/lib/defaults';
import { db } from '@/lib/firebase';
import { collection, getDocs } from 'firebase/firestore';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };

export default function FAQ() {
  const [faqs, setFaqs] = useState(defaultFAQs);

  useEffect(() => {
    async function load() {
      try {
        const snap = await getDocs(collection(db, 'faqs'));
        if (!snap.empty) {
          const items = snap.docs.map(d => ({ ...d.data(), id: d.id }));
          if (items.length > 0) setFaqs(items);
        }
      } catch (e) { /* use defaults */ }
    }
    load();
  }, []);

  return (
    <div data-testid="faq-page" className="bg-white">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <motion.div initial="hidden" animate="visible" variants={fadeUp} transition={{ duration: 0.5 }} className="max-w-3xl mb-16">
          <span className="inline-block px-4 py-1.5 rounded-full bg-[#F5F0F7] text-[#4B1D5E] text-sm font-medium mb-6">FAQ</span>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-[#2D1B36] mb-6">Frequently Asked Questions</h1>
          <p className="text-base sm:text-lg text-[#5E4B66]">
            Find answers to common questions about singing lessons at Singvolve.
          </p>
        </motion.div>

        <div className="max-w-3xl">
          <Accordion type="single" collapsible className="space-y-4" data-testid="faq-accordion">
            {faqs.map((faq) => (
              <AccordionItem
                key={faq.id}
                value={faq.id}
                className="bg-white border border-purple-100 rounded-2xl px-6 shadow-sm data-[state=open]:shadow-md transition-shadow"
              >
                <AccordionTrigger className="text-left font-heading font-semibold text-[#2D1B36] hover:text-[#4B1D5E] py-5 text-base" data-testid={`faq-trigger-${faq.id}`}>
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-[#5E4B66] text-sm leading-relaxed pb-5">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          <div className="mt-12 bg-[#F5F0F7] rounded-2xl p-8 text-center">
            <p className="text-[#2D1B36] font-heading font-semibold text-lg mb-2">Still have questions?</p>
            <p className="text-sm text-[#5E4B66] mb-6">We'd love to hear from you. Get in touch and we'll get back to you as soon as possible.</p>
            <Link to="/contact" data-testid="faq-contact-cta">
              <Button className="bg-[#4B1D5E] hover:bg-[#3A164A] text-white rounded-full px-8 py-5 font-semibold">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
